import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Home, Package, ShoppingBag, Search, Copy, Check } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/common/Button';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { getPaymentMethodLabel } from '../../utils/constants';
import { useToast } from '../../context/ToastContext';

const STORAGE_KEY = 'lastOrderInfo';

const OrderCompletePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess } = useToast();

  // state → sessionStorage 순으로 주문 정보 복원 (새로고침 소실 방지)
  const [orderInfo] = useState(() => {
    if (location.state?.orderInfo) {
      return location.state.orderInfo;
    }
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('주문 정보 복원 실패:', e);
      return null;
    }
  });

  const [copied, setCopied] = useState(false);

  // state 로 들어온 주문 정보를 sessionStorage 에 저장
  useEffect(() => {
    if (location.state?.orderInfo) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(location.state.orderInfo));
      } catch (e) {
        console.error('주문 정보 저장 실패:', e);
      }
    }
  }, [location.state]);

  // B9: 렌더 중 navigate 대신 useEffect 에서 리다이렉트
  useEffect(() => {
    if (!orderInfo) {
      navigate('/', { replace: true });
    }
  }, [orderInfo, navigate]);

  if (!orderInfo) {
    return null;
  }

  const orderId = orderInfo.orderId;
  const orderedAt = orderInfo.orderedAt || orderInfo.orderDate;
  const items = orderInfo.items || [];

  const handleCopyOrderId = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(orderId);
      } else {
        // 폴백
        const textarea = document.createElement('textarea');
        textarea.value = orderId;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      showSuccess('주문번호를 복사했습니다.');
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('복사 실패:', e);
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-2xl mx-auto">
          {/* 완료 아이콘 및 메시지 */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary mb-2">
              주문이 완료되었습니다
            </h1>
            <p className="text-accent-600">주문하신 화환이 2-3시간 내 배송될 예정입니다</p>
          </div>

          {/* 주문 번호 */}
          <div className="bg-primary-50 rounded-lg border border-primary-200 p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm text-accent-600 mb-1">주문번호</p>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-bold text-primary">{orderId}</p>
                  <button
                    onClick={handleCopyOrderId}
                    className="inline-flex items-center gap-1 text-xs text-accent-600 hover:text-primary border border-accent-300 rounded-md px-2 py-1 transition-colors"
                    aria-label="주문번호 복사"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? '복사됨' : '복사'}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-accent-600 mb-1">주문일시</p>
                <p className="text-sm font-medium text-primary">
                  {orderedAt ? formatDateTime(orderedAt) : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* 주문 정보 */}
          <div className="bg-white rounded-lg border border-accent-200 p-6 mb-6 space-y-6">
            {/* 주문 상품 목록 */}
            {items.length > 0 && (
              <>
                <div>
                  <h2 className="text-lg font-bold text-primary mb-3 flex items-center gap-2">
                    <ShoppingBag size={20} />
                    주문 상품
                  </h2>
                  <div className="space-y-3">
                    {items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 pb-3 border-b border-accent-100 last:border-0"
                      >
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.wreathName}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = '/images/wreaths/placeholder.svg';
                            }}
                            className="w-14 h-14 rounded-md object-cover bg-accent-100"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-primary truncate">
                            {item.wreathName}
                          </p>
                          <p className="text-xs text-accent-600">
                            {item.quantity}개 × {formatCurrency(item.price)}
                          </p>
                        </div>
                        <p className="text-sm font-bold text-primary">
                          {formatCurrency(item.price * item.quantity)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t border-accent-200"></div>
              </>
            )}

            {/* 주문자 정보 */}
            <div>
              <h2 className="text-lg font-bold text-primary mb-3 flex items-center gap-2">
                <Package size={20} />
                보내는 분 정보
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-accent-600">이름</span>
                  <span className="font-medium text-primary">{orderInfo.senderName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-accent-600">연락처</span>
                  <span className="font-medium text-primary">{orderInfo.ordererPhone}</span>
                </div>
              </div>
            </div>

            <div className="border-t border-accent-200"></div>

            {/* 배송지 정보 */}
            <div>
              <h2 className="text-lg font-bold text-primary mb-3 flex items-center gap-2">
                <ShoppingBag size={20} />
                배송지 정보
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-accent-600">장례식장</span>
                  <span className="font-medium text-primary">
                    {orderInfo.funeralHome}
                    {orderInfo.funeralRoom ? ` ${orderInfo.funeralRoom}` : ''}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-accent-600">주소</span>
                  <span className="font-medium text-primary text-right max-w-[60%]">
                    {orderInfo.address}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-accent-600">고인명</span>
                  <span className="font-medium text-primary">{orderInfo.deceasedName}</span>
                </div>
              </div>
            </div>

            {/* 메시지 카드 / 리본 */}
            {(orderInfo.messageCard ||
              (orderInfo.ribbon &&
                (orderInfo.ribbon.left || orderInfo.ribbon.right))) && (
              <>
                <div className="border-t border-accent-200"></div>
                <div>
                  <h2 className="text-lg font-bold text-primary mb-3">메시지 / 근조 리본</h2>
                  <div className="space-y-2 text-sm">
                    {orderInfo.ribbon?.left && (
                      <div className="flex items-center justify-between">
                        <span className="text-accent-600">왼쪽 리본</span>
                        <span className="font-medium text-primary">{orderInfo.ribbon.left}</span>
                      </div>
                    )}
                    {orderInfo.ribbon?.right && (
                      <div className="flex items-center justify-between">
                        <span className="text-accent-600">오른쪽 리본</span>
                        <span className="font-medium text-primary">{orderInfo.ribbon.right}</span>
                      </div>
                    )}
                    {orderInfo.messageCard && (
                      <div className="mt-2 p-3 bg-accent-50 rounded-lg border border-accent-200">
                        <p className="text-xs text-accent-600 mb-1">메시지 카드</p>
                        <p className="text-sm text-primary whitespace-pre-line">
                          {orderInfo.messageCard}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="border-t border-accent-200"></div>

            {/* 결제 정보 */}
            <div>
              <h2 className="text-lg font-bold text-primary mb-3">결제 정보</h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-accent-600">결제 방법</span>
                  <span className="font-medium text-primary">
                    {getPaymentMethodLabel(orderInfo.paymentMethod)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-accent-600">결제 금액</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(orderInfo.totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 안내 사항 */}
          <div className="bg-accent-50 rounded-lg border border-accent-200 p-6 mb-6">
            <h3 className="font-bold text-primary mb-3">배송 안내</h3>
            <ul className="text-sm text-accent-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5"></span>
                <span>주문하신 화환은 2-3시간 내 배송될 예정입니다</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5"></span>
                <span>배송 전 담당자가 연락 드립니다</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5"></span>
                <span>장례식장으로 직접 배송됩니다</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5"></span>
                <span>문의사항은 고객센터(1234-5678)로 연락주세요</span>
              </li>
            </ul>
          </div>

          {/* 버튼 그룹 */}
          <div className="space-y-3">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => navigate(`/order-search?orderId=${encodeURIComponent(orderId)}`)}
              className="flex items-center justify-center gap-2"
            >
              <Search size={20} />
              주문 조회 / 배송 현황
            </Button>

            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={() => navigate('/mypage/orders')}
              className="flex items-center justify-center gap-2"
            >
              <Package size={20} />
              주문 내역 보기
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" size="md" fullWidth onClick={() => navigate('/wreaths')}>
                화환 더 보기
              </Button>
              <Button
                variant="ghost"
                size="md"
                fullWidth
                onClick={() => navigate('/')}
                className="flex items-center justify-center gap-2"
              >
                <Home size={18} />
                홈으로
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default OrderCompletePage;
